import { useCallback, useMemo } from 'react';

import { IconButtons } from './IconButtons';
import type { IconButtonsOption } from './IconButtonsOption';

type Props<TValue extends string> = {
  id?: string;
  name: string;
  values: TValue[] | undefined;
  onChange: (value: TValue[]) => void;
  options: IconButtonsOption<TValue>[];
  qaId?: string;
};

export function IconCheckboxButtons<TValue extends string>({
  id,
  name,
  values: valuesProp,
  onChange,
  options,
  qaId,
}: Props<TValue>) {
  const values = useMemo(() => valuesProp ?? [], [valuesProp]);

  const isOptionSelected = useCallback(
    (option: IconButtonsOption<TValue>) => values.includes(option.value),
    [values],
  );

  const toggle = useCallback(
    (value: TValue) => {
      const newValues = [...values];
      const index = newValues.indexOf(value);

      if (index === -1) {
        newValues.push(value);
      } else {
        newValues.splice(index, 1);
      }

      onChange(newValues);
    },
    [onChange, values],
  );

  const renderInput = useCallback(
    (option: IconButtonsOption<TValue>) => (
      <input
        type="checkbox"
        name={name}
        value={option.value}
        onChange={() => toggle(option.value)}
        style={{ display: 'none' }}
        checked={isOptionSelected(option)}
      />
    ),
    [toggle, name, isOptionSelected],
  );

  return (
    <IconButtons
      id={id}
      options={options}
      isOptionSelected={isOptionSelected}
      renderInput={renderInput}
      qaId={qaId}
    />
  );
}
